import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { GeoPoint } from "firebase/firestore";
import { getNearMeLocations } from "./offersFirestore";
import LocationCard from "../location/locationCard";
import FilterSelector from "../location/filterSelector";
import type { Location } from "../location/locationCard";

const NearMePage: React.FC = () => {
  const [currentPos, setCurrentPos] = useState<GeoPoint | null>(null);
  const [radius, setRadius] = useState<number>(() => {
    const saved = sessionStorage.getItem("distance");
    return saved ? Number(saved) : 2;
  });
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // For filtering
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showClosed, setShowClosed] = useState<boolean>(
    localStorage.getItem("showClosed") === "true" || false
  );

  // 1) Helper: fetch Locations
  const fetchLocations = async (geoPoint: GeoPoint, distance: number) => {
    try {
      setLoading(true);
      const results = await getNearMeLocations(geoPoint, distance);
      setLocations(results);

      localStorage.setItem("cachedLocations", JSON.stringify(results));

      if (results.length === 0) {
        setError("No locations found within the selected radius.");
      } else {
        setError(null);
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching locations.");
    } finally {
      setLoading(false);
    }
  };

  // 2) Request user's current location
  const requestLocation = () => {
    setError(null);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          const geoPoint = new GeoPoint(latitude, longitude);

          setCurrentPos(geoPoint);
          localStorage.setItem("lastLat", String(latitude));
          localStorage.setItem("lastLng", String(longitude));

          // fetch locations with fresh location
          fetchLocations(geoPoint, radius);
        },
        (err) => {
          console.error("Geolocation error:", err);
          setError("Unable to retrieve location. Please allow permission.");
        },
        {
          maximumAge: 60000,
          timeout: 15000,
          enableHighAccuracy: false,
        }
      );
    } else {
      setError("Geolocation not supported in this browser.");
    }
  };

  // 3) On mount, check cached data in localStorage
  useEffect(() => {
    const savedLat = localStorage.getItem("lastLat");
    const savedLng = localStorage.getItem("lastLng");
    if (savedLat && savedLng) {
      const latNum = Number(savedLat);
      const lngNum = Number(savedLng);
      setCurrentPos(new GeoPoint(latNum, lngNum));
    }

    const cachedLocationsString = localStorage.getItem("cachedLocations");
    if (cachedLocationsString) {
      try {
        const parsedLocations: Location[] = JSON.parse(cachedLocationsString);
        setLocations(parsedLocations);
      } catch (e) {
        console.error("Failed to parse cached Locations:", e);
      }
    }
    requestLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whenever radius or currentPos changes, refetch
  useEffect(() => {
    if (currentPos) {
      fetchLocations(currentPos, radius);
    }
  }, [radius, currentPos]);

  // 4) Filter the locations array
  const filteredLocations = locations.filter((loc) => {
    const nameMatch = loc.name.toLowerCase().includes(searchTerm.toLowerCase());
    const tagsMatch =
      selectedTags.length === 0
        ? true
        : loc.tags
        ? selectedTags.some((tag) => loc.tags.includes(tag))
        : false;

    // Simple "open now" check
    const isOpenNow = (() => {
      const today = new Date().toLocaleDateString("en-US", { weekday: "long" });
      const storeHours = loc.storeHours[today];
      if (!storeHours) return false;
      const { open, close } = storeHours;
      if (open === "00:00" && close === "00:00") return false;
      const [openHour, openMinute] = open.split(":").map(Number);
      const [closeHour, closeMinute] = close.split(":").map(Number);
      const now = new Date();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const openMinutes = openHour * 60 + openMinute;
      const closeMinutes = closeHour * 60 + closeMinute;
      return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
    })();

    // If showClosed is false, only show open
    const closedMatch = showClosed ? true : isOpenNow;

    return nameMatch && tagsMatch && closedMatch;
  });

  // 5) Render
  return (
    <Box
      sx={{
        backgroundColor: "#0D1B2A",
        padding: 2,
        minHeight: "100vh",
        color: "white",
      }}
    >
      <Typography variant="h4" align="center" gutterBottom>
        Discounts Near Me
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
        }}
      >
        <FilterSelector
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          showClosed={showClosed}
          setShowClosed={setShowClosed}
          distance={radius}
          setDistance={setRadius}
          onLocationClick={requestLocation}
        />
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography variant="body2" color="error" align="center" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {/* Render the filtered list of locations */}
      {filteredLocations.map((loc, index) => (
        <LocationCard key={index} location={loc} />
      ))}
    </Box>
  );
};

export default NearMePage;
